/**
 * CountUp — spring-animated number counter triggered on scroll.
 *
 * Inspired by React Bits. Uses Framer Motion's spring + useInView.
 */

import { motion, useInView, useMotionValue, useSpring, useTransform } from 'framer-motion'
import { useEffect, useRef } from 'react'

interface CountUpProps {
  /** Target number */
  to: number
  /** Starting number. Default: 0 */
  from?: number
  /** Duration in seconds. Default: 2 */
  duration?: number
  /** Delay before starting in seconds. Default: 0 */
  delay?: number
  /** Thousand separator. Default: ',' */
  separator?: string
  /** Number of decimal places. Default: 0 */
  decimals?: number
  /** Prefix string (e.g. '€') */
  prefix?: string
  /** Suffix string (e.g. '%') */
  suffix?: string
  className?: string
  /** Only start when this is true. Default: true */
  startWhen?: boolean
  onStart?: () => void
  onEnd?: () => void
}

export function CountUp({
  to,
  from = 0,
  duration = 2,
  delay = 0,
  separator = ',',
  decimals = 0,
  prefix = '',
  suffix = '',
  className,
  startWhen = true,
  onStart,
  onEnd,
}: CountUpProps) {
  const ref = useRef<HTMLSpanElement>(null)
  const inView = useInView(ref, { once: true, margin: '-40px' })
  const shouldAnimate = inView && startWhen

  const motionValue = useMotionValue(from)
  const springValue = useSpring(motionValue, {
    duration: duration * 1000,
    bounce: 0,
  })

  const display = useTransform(springValue, (latest) => {
    const fixed = latest.toFixed(decimals)
    const [intPart, decPart] = fixed.split('.')
    const formatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, separator)
    return `${prefix}${formatted}${decPart ? `.${decPart}` : ''}${suffix}`
  })

  useEffect(() => {
    if (!shouldAnimate) return
    const timer = setTimeout(() => {
      onStart?.()
      motionValue.set(to)
    }, delay * 1000)
    return () => clearTimeout(timer)
  }, [shouldAnimate, to, delay, motionValue, onStart])

  useEffect(() => {
    if (!shouldAnimate) return
    const unsubscribe = springValue.on('change', (v) => {
      if (Math.abs(v - to) < (decimals > 0 ? 0.01 : 0.5)) {
        onEnd?.()
        unsubscribe()
      }
    })
    return unsubscribe
  }, [shouldAnimate, springValue, to, decimals, onEnd])

  return (
    <motion.span ref={ref} className={className}>
      {display}
    </motion.span>
  )
}
